<script lang="ts" setup>
import { computed } from 'vue';

import { NutritionFacts as NutritionFactsType } from '@/api/productNutritionFacts';
import { TagReference } from '@/api/productTagReferences';
import Gallery, {
  CarouselItem,
  GalleryDataPromo,
} from '@/components/base/image-gallery/Gallery.vue';
import TagBadge from '@/components/base/TagBadge.vue';
import BagSizeBadge from '@/components/pdp/BagSizeBadge.vue';
import GalleryProductDetails from '@/components/pdp/GalleryProductDetails.vue';
import OutOfStockBadge from '@/components/pdp/OutOfStockBadge.vue';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { isPpaBagSize } from '@/lib/product/ppa';
import { GREATEST_HITS_TAG_NAME } from '@/lib/product/tags';
import { TypographyTags } from '@/utils/accessibility';
import { brandingColorsByCategory } from '@/utils/brandingColorsByCategory';
import { ImageBySize } from '@/utils/image';
import { isDefined } from '@/utils/isDefined';
import { NutsProductVariant } from '@/utils/product';

const DEFAULT_PACKAGE_SIZE_IMAGE_INDEX = 1;

interface PackageSizeImage {
  alt?: string;
  image: string;
  miniature?: string;
  type: string;
}

const {
  showBagSizeBadge = true,
  showPackageSizeImage = true,
  showTagBadge = true,
  ...props
} = defineProps<{
  dataPromos?: GalleryDataPromo;
  headerTag?: TypographyTags;
  images: ImageBySize[];
  isGift?: boolean;
  nutritionFacts?: NutritionFactsType;
  productName?: string;
  showBagSizeBadge?: boolean;
  showPackageSizeImage?: boolean;
  showTagBadge?: boolean;
  tagNames: string[];
  tagsReferences?: TagReference[];
  titleImage?: ImageBySize;
  variant: NutsProductVariant;
}>();

const { flags } = useFeatureFlags();

const allPackageSizeImages: PackageSizeImage[] = [
  {
    image: 'https://nuts.com/images/pdp/2023/M.1194ef04.jpg',
    type: '1 Pound Bag - Medium',
  },
  {
    image: 'https://nuts.com/images/pdp/2023/W.15ebeb58.jpg',
    type: '5 Pound Bag - White',
  },
  {
    image: 'https://nuts.com/images/pdp/2023/S.a259144e.jpg',
    type: '1 Pound Bag - Small',
  },
  {
    image: 'https://nuts.com/images/pdp/2023/L.b0969f31.jpg',
    type: '1 Pound Bag - Large',
  },
  {
    image: 'https://nuts.com/images/pdp/2023/B.aeb90987.jpg',
    type: '5 Pound Bag - Brown',
  },
  {
    image: 'https://nuts.com/images/pdp/2023/LB.641dd1de.jpg',
    type: 'Large Black Bag',
  },
  {
    image: 'https://nuts.com/images/pdp/2023/SB.a4f53ccf.jpg',
    type: 'Small Black Bag',
  },
  {
    image: 'https://nuts.com/images/pdp/2023/TE.c447bb7d.jpg',
    type: '16 Ounce Tamper Evident Container',
  },
  {
    image: 'https://nuts.com/images/pdp/2023/SD.4f56384b.jpg',
    type: '24 Ounce Square Container',
  },
  {
    image: 'https://nuts.com/images/pdp/2023/RD.ddec36ed.jpg',
    type: '48 Ounce Round Container',
  },
  {
    image: 'https://nuts.com/images/pdp/2025/DFLB.cf5f2acc.jpg',
    type: 'DFLB',
  },
  {
    image: 'https://nuts.com/images/pdp/2025/CSS.4574a684.jpg',
    type: 'CSS',
  },
  {
    image: 'https://nuts.com/images/pdp/2025/SNS.ffe2e162.jpg',
    type: 'SNS',
  },
  {
    image: 'https://nuts.com/images/pdp/2025/NSM.f29f8da3.jpg',
    type: 'NSM',
  },
  {
    image: 'https://nuts.com/images/pdp/2025/DFSB.9555a013.jpg',
    type: 'DFSB',
  },
  {
    image: 'https://nuts.com/images/pdp/2025/DFS.9555a013.jpg',
    type: 'DFS',
  },
  {
    image: 'https://nuts.com/images/pdp/2025/Chocolate-Bulk.7f227704.jpg',
    type: 'Chocolate Bulk',
  },
  {
    image: 'https://nuts.com/images/pdp/2025/CSSB.66b68272.jpg',
    type: 'CSSB',
  },
  {
    image: 'https://nuts.com/images/pdp/2025/SNLB.a7227e1b.jpg',
    type: 'SNLB',
  },
  {
    image: 'https://nuts.com/images/pdp/2025/DFL.bff84980.jpg',
    type: 'DFL',
  },
  {
    image: 'https://nuts.com/images/pdp/2025/Nuts-Bulk.f6085d99.jpg',
    type: 'Nuts Bulk',
  },
  {
    image: 'https://nuts.com/images/pdp/2025/DriedFruit-Bulk.1c98623c.jpg',
    type: 'Dried Fruit Bulk',
  },
  {
    image: 'https://nuts.com/images/pdp/2025/NSLB.607e67f3.jpg',
    type: 'NSLB',
  },
  {
    image: 'https://nuts.com/images/pdp/2025/Snacks-Bulk.aa6a6403.jpg',
    type: 'Snacks Bulk',
  },
  {
    image: 'https://nuts.com/images/pdp/2025/SNL.ba290101.jpg',
    type: 'SNL',
  },
  {
    image: 'https://nuts.com/images/pdp/2025/NSSB.f7953a9e.jpg',
    type: 'NSSB',
  },
  {
    image: 'https://nuts.com/images/pdp/2025/CSL.3ce36a02.jpg',
    type: 'CSL',
  },
];

const packageSizeImage = computed<PackageSizeImage | undefined>(() =>
  allPackageSizeImages.find((image) => image.type === props.variant.bagSize?.key),
);

const isGreatestHits = computed(() => props.tagNames.includes(GREATEST_HITS_TAG_NAME));
const isPpaVariant = computed(() => isPpaBagSize(props.variant.bagSize));

const hasNutritionFacts = computed(() =>
  props.nutritionFacts
    ? Object.values(props.nutritionFacts.perServing).some(
        (nutritionFact) => nutritionFact.pdv || nutritionFact.quantity,
      )
    : false,
);

const L1Category = computed(() => props.variant.reportingCategory?.split('>')[0].trim());

const carouselSlides = computed<CarouselItem[]>(() => {
  const images: CarouselItem[] = props.images.map((image) => ({
    alt: `${props.productName} photo`,
    content: image.large,
    miniature: image.small,
    type: 'image',
  }));

  if (!props.isGift) {
    if (packageSizeImage.value && showPackageSizeImage) {
      const packageSizeImageItem: CarouselItem = {
        alt: packageSizeImage.value.alt || `Bag size - ${props.productName} photo`,
        content: packageSizeImage.value.image,
        miniature: packageSizeImage.value.miniature || packageSizeImage.value.image,
        type: 'image',
      };

      if (isGreatestHits.value && !isPpaVariant.value) {
        images.push(packageSizeImageItem);
      } else {
        images.splice(DEFAULT_PACKAGE_SIZE_IMAGE_INDEX, 0, packageSizeImageItem);
      }
    }

    if (hasNutritionFacts.value) {
      images.push({
        content: GalleryProductDetails,
        miniature: 'https://nuts.com/images/pdp/2025/blurred-nutritional-facts.3319596e.png',
        thumbnailBgClass:
          (flags.categoryColors && brandingColorsByCategory[L1Category.value ?? '']?.background) ||
          'bg-nuts-orange-50',
        type: 'html',
      });
    }
  }

  return images;
});

const packageSizeImageIndex = computed(() => {
  if (isGreatestHits.value && !isPpaVariant.value) {
    // If nutrition facts exist, bag size is second to last, otherwise last
    return hasNutritionFacts.value
      ? carouselSlides.value.length - 2
      : carouselSlides.value.length - 1;
  }
  return DEFAULT_PACKAGE_SIZE_IMAGE_INDEX;
});
</script>

<template>
  <Gallery
    :carouselSlides="carouselSlides"
    :dataPromos="dataPromos"
    :headerTag
    :title="productName"
  >
    <template v-slot="{ isMobile, slide, scale }">
      <Component
        :is="slide.content"
        :isMobile
        :nutritionFacts="isGift ? undefined : nutritionFacts"
        :productName
        :style="{ scale }"
        :tagsReferences
        :variant
      />
    </template>
    <template #badge="{ index }">
      <template v-if="!nutritionFacts || (nutritionFacts && index !== carouselSlides.length - 1)">
        <OutOfStockBadge
          v-if="variant.backordered"
          class="absolute top-0 -left-10 z-10 sm:hidden"
        />
        <TagBadge
          v-if="!isDefined(flags.socialProofMessaging) && showTagBadge"
          class="absolute top-0 right-4 ws-md:right-0 lg:top-1.5"
          :tags="tagNames"
        />
      </template>
      <transition name="fade">
        <BagSizeBadge
          v-if="
            packageSizeImage &&
            variant.unitName &&
            index === packageSizeImageIndex &&
            showBagSizeBadge &&
            !isPpaVariant
          "
          :bagSize="packageSizeImage.type"
          class="absolute z-10 top-[15%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 scale-50 xl:scale-75"
          :unitName="variant.unitName"
        />
      </transition>
    </template>
  </Gallery>
</template>
